import { isExistingStandaloneJPDProduct, isOfferingBelongsToJiraFamily, isOfferingIncluded } from '@css-xp/ppc-utils';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { stitchChangeKeyObjects, transformAndStitchAppChangedKeyObjects } from '../utils';
import { truthy } from '../../utils';
import type { ProductIdPlanOptionsMap } from '../../ui/product-line-items/src/types';
import type { OfferingAndPricingPlan, State } from '../types';

function isSandbox(text?: string | null) {
  const sandboxRegex = /Sandbox/gi;
  return sandboxRegex.test(text || '');
}

const getOfferingsDetailsForPricingPlanMap = (eligibleOfferings: OfferingAndPricingPlan[]) => {
  const offeringsForPresentation = eligibleOfferings?.map(({ id, offeringName, defaultPricingPlan, level }) => {
    return {
      offeringName: offeringName as string,
      pricingPlanId: defaultPricingPlan?.id as string,
      offeringId: id,
      offeringLevel: level,
    };
  });

  return offeringsForPresentation;
};

export function getPricingPlanOptions(state: State): ProductIdPlanOptionsMap {
  if (!state.offeringsAndpricingPlans) return {};
  const isJSMStandardOfferingEnabled = state?.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_JSM_OFFERING_CHANGE];
  const isCompassPremiumEnabled = state?.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_COMPASS_PREMIUM];
  const isJPDPremiumOfferingEnabled = state?.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_JPD_PREMIUM_OFFERING];
  const isJPDAsJiraFamilyEnabled = state?.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY];
  const isAppEditionSupportEnabled = state?.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT];
  const selectedProducts = Object.values(stitchChangeKeyObjects(state.products, state.selectedProducts));
  const selectedApps = Object.values(
    transformAndStitchAppChangedKeyObjects(state.linkedEntitlements, state.selectedLinkedEntitlements)
  );

  const productIdPlanOptionsMap: ProductIdPlanOptionsMap = {};

  selectedProducts.forEach((product) => {
    const { id, productId } = product;
    const correspondingOffering = state.offeringsAndpricingPlans?.[productId] || [];

    /** checking if the existing offering id belongs to Jira Family or Standalone offering.
     * If offering id belongs to Jira Family, then we need to check if the Jira Family offering is enabled or not
     * Based on this condition we can switch standalone offerings and JPD family offerings
     */
    const isJPDOfferingBelongsToJiraFamily =
      isJPDAsJiraFamilyEnabled && !isExistingStandaloneJPDProduct(state.products[id]?.offeringId);

    const nonEmptyOfferings = correspondingOffering.filter(truthy);
    const eligibleOfferings = nonEmptyOfferings.filter((offering) =>
      isOfferingIncluded(offering, {
        isEnterpriseOfferingEnabled: false,
        isJSMRepackagingEnabled: isJSMStandardOfferingEnabled,
        isCompassPremiumEnabled,
        isJPDPremiumOfferingEnabled,
        isJPDAsJiraFamilyEnabled: isJPDOfferingBelongsToJiraFamily,
        isAppEditionSupportEnabled: isAppEditionSupportEnabled,
      })
    );

    const offeringsForPresentation = getOfferingsDetailsForPricingPlanMap(eligibleOfferings);
    productIdPlanOptionsMap[id] = offeringsForPresentation;
  });

  isAppEditionSupportEnabled &&
    selectedApps.forEach((app: any) => {
      const { id, productId } = app;
      const correspondingOffering = state.offeringsAndpricingPlans?.[productId] || [];

      const nonEmptyOfferings = correspondingOffering.filter(truthy);
      const eligibleOfferings = nonEmptyOfferings.filter((offering) =>
        isOfferingIncluded(offering, {
          isAppEditionSupportEnabled: isAppEditionSupportEnabled,
        })
      );

      const offeringsForPresentation = getOfferingsDetailsForPricingPlanMap(eligibleOfferings);
      productIdPlanOptionsMap[id] = offeringsForPresentation;
    });

  return productIdPlanOptionsMap;
}
